<template>
    <transition name="sip-search-overlay">
        <div
            v-if="searchOverlay"
            class="sip-global-search-overlay search-overlay"
            role="dialog"
            aria-modal="true"
            :aria-label="trans('general.any_search_title')"
            @mousedown.self="closeSearch"
        >
            <div class="sip-global-search-panel">
                <div class="sip-global-search-panel__header">
                    <div class="sip-global-search-input-wrap">
                        <em class="icon ni ni-search sip-global-search-input__icon" aria-hidden="true"></em>
                        <input
                            type="text"
                            ref="searchTermElement"
                            v-model="search"
                            :placeholder="trans('general.any_search_hint')"
                            spellcheck="false"
                            autocomplete="off"
                            class="sip-global-search-input search-term"
                        />
                        <button
                            type="button"
                            class="sip-global-search-close"
                            :aria-label="trans('general.close')"
                            @click="closeSearch"
                        >
                            <em class="icon ni ni-cross" aria-hidden="true"></em>
                        </button>
                    </div>
                    <div class="sip-global-search-hint" :class="'is-' + searchHintState">
                        <template v-if="searchHintState === 'min-chars'">
                            {{ trans('general.any_search_type_atleast_3_characters') }}
                        </template>
                        <template v-else-if="searchHintState === 'loading'">
                            <span class="sip-global-search-hint__loading">
                                <span class="sip-global-search-hint__spinner" aria-hidden="true"></span>
                                {{ trans('general.any_search_loading') }}
                            </span>
                        </template>
                        <template v-else-if="searchHintState === 'empty'">
                            {{ trans('general.no_search_result_found') }}
                        </template>
                        <template v-else>
                            <span class="sip-global-search-hint__count">
                                {{ searchResultCount }}
                                <span class="sip-global-search-hint__label">{{ trans('general.any_search_loaded').split(',')[0] }}</span>
                            </span>
                            <span class="sip-global-search-hint__kbd">
                                {{ keyboardHint }}
                            </span>
                        </template>
                    </div>
                </div>

                <div
                    class="sip-global-search-panel__body"
                    v-if="student_search_results.length || employee_search_results.length"
                >
                    <div class="sip-global-search-columns">
                        <section class="sip-global-search-section" v-if="student_search_results.length">
                            <h3 class="sip-global-search-section__title">{{ trans('student.student') }}</h3>
                            <div class="sip-global-search-results">
                                <article
                                    class="sip-search-result-card"
                                    v-for="(student_record, index) in student_search_results"
                                    :key="student_record.student.uuid"
                                    :style="{ '--sip-search-stagger': index }"
                                >
                                    <div class="sip-search-result-card__inner">
                                        <div class="sip-profile-card__head">
                                            <div class="sip-profile-card__avatar">
                                                <template v-if="!student_record.student.student_photo">
                                                    <img
                                                        v-if="student_record.student.gender == 'female'"
                                                        :src="getAbsoluteUrl('images/avatar_female_kid.png')"
                                                        alt=""
                                                    >
                                                    <img v-else :src="getAbsoluteUrl('images/avatar_male_kid.png')" alt="">
                                                </template>
                                                <template v-else>
                                                    <img :src="getImageUrl(student_record.student.student_photo)" alt="">
                                                </template>
                                            </div>
                                            <div class="sip-profile-card__content">
                                                <div class="sip-profile-card__title-row">
                                                    <h4 class="sip-profile-card__name">{{ student_record.student.name }}</h4>
                                                </div>
                                                <div class="sip-profile-card__meta">
                                                    <span>{{ student_record.admission.admission_number }}</span>
                                                    <template v-if="student_record.student.age">
                                                        <span class="sip-profile-card__meta-sep">·</span>
                                                        <span>{{ student_record.student.age.years + ' ' + trans('list.year') + ' ' + student_record.student.age.months + ' ' + trans('list.month') }}</span>
                                                    </template>
                                                </div>
                                                <div class="sip-profile-card__detail">
                                                    {{ student_record.batch.course.name + ' ' + student_record.batch.name }} ({{ student_record.full_roll_number }})
                                                </div>
                                            </div>
                                        </div>
                                        <div class="sip-profile-card__footer">
                                            <span v-if="student_record.student.parent">{{ student_record.student.parent.first_guardian_name }}</span>
                                            <span v-if="student_record.student.contact_number">
                                                <em class="icon ni ni-mobile"></em> {{ student_record.student.contact_number }}
                                            </span>
                                        </div>
                                        <div class="sip-search-result-card__actions">
                                            <button type="button" class="btn btn-primary btn-sm" @click="navigateToStudent(student_record.student.uuid)">
                                                <em class="icon ni ni-eye-alt"></em> {{ trans('general.view') }}
                                            </button>
                                            <button
                                                v-if="hasPermission('list-student-fee')"
                                                type="button"
                                                class="btn btn-outline-primary btn-sm"
                                                @click="navigateToStudentFee(student_record.id, student_record.student.uuid)"
                                            >
                                                <em class="icon ni ni-wallet"></em> {{ trans('finance.view_fee_allocation') }}
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            </div>
                        </section>

                        <section class="sip-global-search-section" v-if="employee_search_results.length">
                            <h3 class="sip-global-search-section__title">{{ trans('employee.employee') }}</h3>
                            <div class="sip-global-search-results">
                                <article
                                    class="sip-search-result-card"
                                    v-for="(employee, index) in employee_search_results"
                                    :key="employee.uuid"
                                    :style="{ '--sip-search-stagger': index }"
                                >
                                    <div class="sip-search-result-card__inner">
                                        <div class="sip-profile-card__head">
                                            <div class="sip-profile-card__avatar">
                                                <template v-if="!employee.photo">
                                                    <img
                                                        v-if="employee.gender == 'female'"
                                                        :src="getAbsoluteUrl('images/avatar_female.png')"
                                                        alt=""
                                                    >
                                                    <img v-else :src="getAbsoluteUrl('images/avatar_male.png')" alt="">
                                                </template>
                                                <template v-else>
                                                    <img :src="getImageUrl(employee.photo)" alt="">
                                                </template>
                                            </div>
                                            <div class="sip-profile-card__content">
                                                <div class="sip-profile-card__title-row">
                                                    <h4 class="sip-profile-card__name">{{ employee.name }}</h4>
                                                    <span class="sip-profile-card__badge" v-html="getStatus(employee)"></span>
                                                </div>
                                                <div class="sip-profile-card__meta">
                                                    <span>{{ employee.employee_code }}</span>
                                                    <template v-if="employee.age">
                                                        <span class="sip-profile-card__meta-sep">·</span>
                                                        <span>{{ employee.age.years + ' ' + trans('list.year') + ' ' + employee.age.months + ' ' + trans('list.month') }}</span>
                                                    </template>
                                                </div>
                                                <div class="sip-profile-card__detail">
                                                    <template v-if="employee.employee_designations.length && employee.employee_designations[0].department_id">
                                                        {{ employee.employee_designations[0].department.name }}
                                                    </template>
                                                    <template v-if="employee.employee_designations.length">
                                                        {{ employee.employee_designations[0].designation.name + ' (' + employee.employee_designations[0].designation.employee_category.name + ')' }}
                                                    </template>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="sip-profile-card__footer">
                                            <span v-if="employee.employee_terms[0]">
                                                {{ trans('general.since') }} {{ employee.employee_terms[0].date_of_joining | moment }}
                                            </span>
                                            <span v-if="employee.contact_number">
                                                <em class="icon ni ni-mobile"></em> {{ employee.contact_number }}
                                            </span>
                                        </div>
                                        <div class="sip-search-result-card__actions">
                                            <button type="button" class="btn btn-primary btn-sm" @click="navigateToEmployee(employee.uuid)">
                                                <em class="icon ni ni-eye-alt"></em> {{ trans('general.view') }}
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
    export default {
        data() {
            return {
                searchOverlay: false,
                search: '',
                student_search_results: [],
                employee_search_results: [],
                resultLoading: false,
                timeout: null
            }
        },
        mounted() {
            let self = this
            window.onkeydown = function (e) {
                if (!e) e = window.event;
                if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
                    e.preventDefault();
                    self.open();
                    return;
                }
                if (!e.metaKey && !self.searchOverlay) {
                    if((e.target.localName === 'body' || e.target.localName === 'document') && (e.keyCode >= 65 && event.keyCode <= 90 || e.keyCode >= 48 && event.keyCode <= 57)) {
                        self.searchOverlay = true
                    }
                }
            }
        },
        created() {
            const handlerEscape = (e) => {
                if (e.key === 'Esc' || e.key === 'Escape') {
                    this.closeSearch()
                }
            }

            document.addEventListener('keydown', handlerEscape)

            this.$once('hook:beforeDestroy', () => {
                document.removeEventListener('keydown', handlerEscape)
            })
        },
        methods: {
            open() {
                this.searchOverlay = true;
                this.$nextTick(() => {
                    const el = this.$refs.searchTermElement;
                    if (el && typeof el.focus === 'function') {
                        el.focus();
                    }
                });
            },
            closeSearch() {
                this.search = ''
                this.searchOverlay = false
                this.student_search_results = []
                this.employee_search_results = []
                this.resultLoading = false
            },
            hasPermission(permission) {
                return helper.hasPermission(permission)
            },
            getAbsoluteUrl(path) {
                return (window.helper && window.helper.getAbsoluteUrl(path)) || ('/' + String(path || '').replace(/^\/+/, ''));
            },
            getImageUrl(path) {
                return (window.helper && window.helper.getImageUrl(path)) || this.getAbsoluteUrl(path);
            },
            searchResult(){
                this.resultLoading = true;

                clearTimeout(this.timeout);
                
                var self = this;
                this.timeout = setTimeout(function () {
                    let loadingGuard = setTimeout(function () {
                        self.resultLoading = false;
                    }, 12000);
                    if (self.search.length < 3) {
                        self.resultLoading = false;
                        clearTimeout(loadingGuard);
                        return;
                    }

                    axios.get('/api/search?q='+encodeURIComponent(self.search.trim()), { timeout: 10000 })
                        .then(response => {
                            const data = (response && response.data) ? response.data : (response || {});
                            self.student_search_results = data.student_records || [];
                            self.employee_search_results = data.employees || [];
                        })
                        .catch(error => {
                            helper.showErrorMsg(error);
                        })
                        .finally(() => {
                            clearTimeout(loadingGuard);
                            self.resultLoading = false;
                        })
                }, 1000);

            },
            getStatus(employee){
                let term = employee.employee_terms;
                if (term.length && term[0].date_of_joining <= helper.today() && (!term[0].date_of_leaving || term[0].date_of_leaving >= helper.today()))
                    return '<span class="badge bg-success">'+i18n.employee.status_active+'</span>';
                else
                    return '<span class="badge bg-danger">'+i18n.employee.status_inactive+'</span>';
            },
            navigateToEmployee(uuid) {
                this.closeSearch()
                this.$router.push('/employee/'+uuid)
            },
            navigateToStudent(uuid) {
                this.closeSearch()
                this.$router.push('/student/'+uuid)
            },
            navigateToStudentFee(id, uuid) {
                this.closeSearch()
                this.$router.push('/student/'+uuid+'/fee/'+id)
            }
        },
        watch: {
            search(val){
                if(val.length >= 3) {
                    this.searchResult();
                } else {
                    this.resultLoading = false
                    this.student_search_results = [];
                    this.employee_search_results = [];
                }
            },
            searchOverlay(isOpen) {
                document.body.classList.toggle('sip-global-search-open', isOpen)
            },
            '$route'() {
                this.closeSearch()
            }
        },
        computed: {
            searchResultCount() {
                return this.student_search_results.length + this.employee_search_results.length
            },
            searchHintState() {
                if (this.search.length < 3) {
                    return 'min-chars'
                }
                if (this.resultLoading) {
                    return 'loading'
                }
                if (this.searchResultCount === 0) {
                    return 'empty'
                }
                return 'results'
            },
            keyboardHint() {
                const parts = i18n.general.any_search_loaded.split(',')
                return parts.length > 1 ? parts[1].trim() : ''
            }
        },
        updated() {
            if (this.searchOverlay && this.$refs.searchTermElement) {
                this.$refs.searchTermElement.focus()
            }
        },
        beforeDestroy() {
            document.body.classList.remove('sip-global-search-open')
        },
        filters: {
          moment(date) {
            return helper.formatDate(date);
          },
          momentDateTime(date) {
            return helper.formatDateTime(date);
          }
        },
    }
</script>
